import { apiClient, isHttpClientError } from "@/http";
import type { ChannelSelection } from "./channelSelection";
import type { JsonObject } from "./common";
import type { DatasetLabel } from "./dataset";
import type { MemoPreview } from "./datasetMemo";
import type { ArmLayout } from "./datasetStructure";
import { type TrainingGroupRef } from "./me";
import type { MemberRef } from "./member";
import type { PageQuery, PageResp } from "./page";
import type { NotificationOverride } from "./trainingNotification";
import { apiRoutes } from "./apiRoutes";

// Training 조회 응답의 식별자는 training-infra가 소유하는 불투명 참조다. registry 가 actor 에서
// 스코프를 도출한다(team=actor 팀 group, me=Admin 이 member 에 매핑한 개인 workspace).
// FE 는 group/workspace 스코프를 직접 다루지 않는다.

// registry 가 그룹 스코프로 training 에 등록(export/submit)한 공용 Dataset 1행. status 는 없다.
// registry 는 training status 를 스냅샷하지 않는다. dataset_uuid 는 학습 제출 입력
// SubmitTrainingSessionReq.datasets 항목의 dataset_uuid와 같은 식별자다. 현재 소비처는 Submit 위저드의
// 데이터셋 picker 하나라 uuid·이름만 읽는다. 나머지 필드는 BE 가 주는 행 계약이다.
export type TrainingDataset = {
  dataset_uuid: string;
  display_name: string;
  // training 등록 참조(표시/딥링크용, 선택).
  training_dataset_id?: string;
  // 대표 섬네일. 대표 source Dataset 의 presigned URL 이다(공용 Dataset·북마크와 같은 계약).
  // 섬네일이 없으면 null 이고 FE 는 이니셜 placeholder 를 표시한다. BE 와 동시에 배포해서 필드는
  // 항상 있다.
  thumbnail_url: string | null;
  thumbnail_url_expires_at: string | null;
  // source Dataset 파생 라벨(공용 카탈로그 노출 판정 미러). 없으면 빈 배열이다.
  labels: readonly DatasetLabel[];
  // embodiment 개요 축. 목록과 같은 계약이다(BE computed·distinct 정렬, 빈 배열 = 미상).
  embodiment_values: readonly string[];
  end_effector_values: readonly string[];
  // 메모(댓글) 요약. 공용 Dataset 목록과 같은 계약이다. BE 가 목록 응답에 항상 포함한다
  // (메모 없으면 count=0·latest=null).
  memo_count: number;
  latest_memo: MemoPreview | null;
};

export type TrainingWorkspace = {
  id: string;
  name: string;
};

// description 은 연구자가 적는 실험 노트다. 목록에 함께 온다. 그래서 실험을 고른 화면은 노트
// 하나를 보려고 다시 조회하지 않는다.
export type TrainingExperiment = {
  id: string;
  workspace_id: string;
  name: string;
  description: string | null;
};

// 실험 노트 본문 상한. training `Experiment.description` 컬럼 폭과 같다.
export const EXPERIMENT_NOTE_MAX_LENGTH = 1024;

// 현재 단계의 실제 진행 스냅샷. stage 는 단계 종류, ratio 는 보고된 완료율(0..1)이다.
// 보고가 없으면 null 이다.
//
// 나머지는 단계가 보고한 값을 서버가 그대로 옮긴 표시용 값이다. 값마다 null 일 수 있다.
export type TrainingSessionProgress = {
  stage: string | null;
  ratio: number | null;
  // 파이프라인 안의 순번(1부터)과 전체 단계 수. 예) train+eval 의 eval 은 2 / 2.
  // 완료율이 아니다. 마지막 단계가 실행 중이어도 순번은 끝 값이다.
  step_order: number | null;
  step_total: number | null;
  // 단계 안의 진행량. 단위는 train 이면 `unit`(epoch 또는 step), eval 이면 에피소드다.
  unit: string | null;
  completed: number | null;
  total: number | null;
  // 아래 넷은 train 단계만 보고한다.
  current_epoch: number | null;
  current_step: number | null;
  // epoch 단위 학습만 보고한다. step 단위 학습에서는 null 이다.
  last_saved_epoch: number | null;
  // 마지막 보고 시점의 남은 시간이다. 종료한 세션에도 값이 남는다. 그래서 실행 중인 세션에서만
  // 남은 시간으로 읽는다.
  eta_seconds: number | null;
};

// 세션을 만든 예약 실행. 직접 제출한 세션에는 없다.
export type TrainingSessionScheduleOrigin = {
  schedule_id: string;
  schedule_name: string;
  occurrence_id: string;
  scheduled_for: string;
};

// 세션 노트 미리보기. 노트가 있는 세션만 값이 있다(없으면 행에서 null).
export type SessionNotePreview = {
  note: string;
  updated_at: string | null;
};

// 세션 목록(내/팀 공용) 행. 서버 row 계약이고, 상세 Window 가 받는 세션 행 모양이다.
// 제출 응답은 trainingSessionFormat 의 submitViewToListView 로 이 모양으로 바꾼다.
// id 는 training session_id 다. status 는 training raw 어휘(queued/running/succeeded/failed/…)다.
// 이름이 없으면 null 이고, 표시는 id 로 폴백한다.
export type TrainingSessionListView = {
  id: string;
  status: string;
  display_name: string | null;
  experiment_id: string | null;
  experiment_name: string | null;
  workspace_id: string | null;
  workspace_name: string | null;
  created_at: string | null;
  // 첫 단계가 시작한 시각과 세션이 끝난 시각. 대기 중이면 둘 다 null 이고, 실행 중이면
  // finished_at 만 null 이다.
  started_at: string | null;
  finished_at: string | null;
  progress: TrainingSessionProgress | null;
  schedule_origin: TrainingSessionScheduleOrigin | null;
  note: SessionNotePreview | null;
  // workspace 선점자 registry 멤버 참조. 선점되지 않은 workspace 의 세션은 null 이다.
  registry_user: MemberRef | null;
  // 제출 입력의 대표 1건. 서버가 registry 제출 저널에서 고른다(학습 타깃 → 첫 train → 첫 제출
  // 항목). 섬네일 서명 URL 은 이 한 건에만 있다. 모든 항목에 서명 URL 을 넣으면, 입력 275개인
  // 세션 50건 페이지에서 서명을 5,000회 해야 했다(4초대). training UI 에서 직접 만든 세션은
  // 저널이 없어 null 이다. 전체 입력은 상세(`TrainingSessionView.datasets`)에 있다.
  dataset: SubmittedDatasetRef | null;
  // 제출 입력 전체 개수. 대표 외 입력 수(`외 N건`)를 이 값으로 계산한다. 저널이 없으면 0 이다.
  dataset_count: number;
  // 대표 순서(학습 타깃 → 첫 train → 첫 제출 항목)의 상위 3건. 첫 항목은 `dataset` 과 같은
  // 입력이다. 섬네일 서명 URL 은 대표에만 있다. 저널이 없으면 빈 배열이다.
  leading_datasets: readonly SubmittedDatasetRef[];
  // 학습 시작점. 상류 init 어휘다(`scratch` / `finetune_from_policy` /
  // `finetune_from_checkpoint` / `resume_from_checkpoint`). 제출 저널이 소유하므로 training UI
  // 에서 직접 만든 세션은 null 이다. null 은 scratch 가 아니라 "모름"이다.
  init_strategy: string | null;
  // 삭제 예정 여부. status 와 독립인 표시 축이다(삭제 예정이어도 실행 상태는 그대로다).
  // 삭제 예정 행은 `includeDeleted` 를 켠 요청에만 온다. **optional 이다.** 구 계약(`archived`)
  // BE 응답에는 이 키가 없다. 키가 없으면 "삭제 예정 아님"으로 처리한다.
  deleted?: boolean;
};

// 이 세션이 삭제 예정인지 판정한다. `deleted` 가 없을 때의 처리는 이 함수만 소유한다.
//
// 처리 규칙은 위 계약 주석이 승인했다. 구 계약 BE 응답에 키가 없으면 삭제 예정이 아니다.
// 소비처마다 `?? false` 로 이 판정을 따로 쓰면, 부재의 뜻이 바뀔 때 일부만 고쳐진다.
// 그래서 판정은 여기서 한 번만 한다.
export function isSessionDeletePending(view: Pick<TrainingSessionListView, "deleted">): boolean {
  return view.deleted ?? false;
}

// 비페이징 목록. 소비처(Submit 위저드의 데이터셋 picker [팀] 소스)가 그룹 전체를 받아 클라이언트
// 에서 좁힌다. 그래서 검색·facet 쿼리는 보내지 않는다.
export async function listTeamTrainingDatasets(groupUuid: string): Promise<TrainingDataset[]> {
  const response = await apiClient.get<TrainingDataset[]>(
    apiRoutes.training.teamDatasets(groupUuid),
  );
  return response.data;
}

// actor 가 멤버인 모든 training 그룹. 그룹 드롭다운과 사이드바 계정 메뉴가 공유하는 단일
// 소스다. 이 목록은 /v1/me 의 linked_training_groups(training_group_id 가 연결된 팀)의
// 상위집합이다. 그래서 제출 화면(DatasetPicker 의 그룹 선택)은 linked_training_groups 를 쓴다.
export async function listMyTrainingGroups(): Promise<TrainingGroupRef[]> {
  const response = await apiClient.get<TrainingGroupRef[]>(apiRoutes.training.meGroups);
  return response.data;
}

// 팀 read 엔드포인트는 group_uuid 를 주면 그 그룹으로 좁힌다. 주지 않으면 actor 의 팀 전체를
// 집계한다. 목록 필터 후보는 workspace 를 직접 보여 주지 않는다. 대신 선점한 workspace 가
// 하나라도 있는 멤버를 돌려준다. BE 가 선택한 멤버의 모든 workspace 를 한 요청 안에서 합친다.
export async function listTeamTrainingMembers(groupUuid?: string): Promise<MemberRef[]> {
  const response = await apiClient.get<MemberRef[]>(apiRoutes.training.teamMembers, {
    params: groupUuid ? { group_uuid: groupUuid } : undefined,
  });
  return response.data;
}

export async function listTeamWorkspaces(groupUuid?: string): Promise<TrainingWorkspace[]> {
  const response = await apiClient.get<TrainingWorkspace[]>(apiRoutes.training.teamWorkspaces, {
    params: groupUuid ? { group_uuid: groupUuid } : undefined,
  });
  return response.data;
}

export async function listTeamExperiments(
  memberUuid?: string,
  groupUuid?: string,
): Promise<TrainingExperiment[]> {
  const params: Record<string, string> = {};
  if (memberUuid) {
    params.member_uuid = memberUuid;
  }
  if (groupUuid) {
    params.group_uuid = groupUuid;
  }
  const response = await apiClient.get<TrainingExperiment[]>(apiRoutes.training.teamExperiments, {
    params,
  });
  return response.data;
}

// me 스코프. Admin 이 actor 에 매핑한 개인 workspace 를 기준으로 한다. 매핑이 없으면 빈 목록이다.
export async function listMyWorkspaces(): Promise<TrainingWorkspace[]> {
  const response = await apiClient.get<TrainingWorkspace[]>(apiRoutes.training.meWorkspaces);
  return response.data;
}

export async function listMyExperiments(): Promise<TrainingExperiment[]> {
  const response = await apiClient.get<TrainingExperiment[]>(apiRoutes.training.meExperiments);
  return response.data;
}

export async function listSharedExperiments(): Promise<TrainingExperiment[]> {
  const response = await apiClient.get<TrainingExperiment[]>(apiRoutes.training.sharedExperiments);
  return response.data;
}

// submit 흐름에서 training UI 로 이동하지 않고 새 experiment 를 만든다. description 은 실험
// 노트(선택)다. BE 가 workspace 스코프를 검증하고 생성 결과(id+이름)를 돌려준다.
// POST /v1/training/experiments.
export type CreateExperimentReq = {
  training_workspace_id: string;
  name: string;
  description?: string | null;
};

export async function createExperiment(req: CreateExperimentReq): Promise<ExperimentOptionView> {
  const response = await apiClient.post<ExperimentOptionView>(
    apiRoutes.training.submitExperiments,
    req,
  );
  return response.data;
}

// 실험 노트만 고친다. 이름은 계약에 없다(training 이 workspace 안에서 unique 로 관리한다).
// null 은 노트를 비운다. BE 가 그 experiment 의 실제 workspace 로 스코프를 판정한다.
export type UpdateExperimentReq = {
  description: string | null;
};

export async function updateExperiment(
  experimentId: string,
  req: UpdateExperimentReq,
): Promise<ExperimentOptionView> {
  const response = await apiClient.patch<ExperimentOptionView>(
    apiRoutes.training.submitExperimentDetail(experimentId),
    req,
  );
  return response.data;
}

// ===========================================================================
// submit 준비 옵션 (submit 화면 초기 진입 + 단계별 선택)
// ===========================================================================

export type BranchOptionView = {
  name: string;
  sha: string;
};

export type TrainerImageOptionView = {
  tag: string;
  image_uri: string;
  version: string;
  is_main_latest: boolean;
};

export type ScenarioOptionView = {
  id: string;
  name: string;
  description: string | null;
};

export type WorkspaceOptionView = {
  training_workspace_id: string;
  name: string;
};

// submit 단계의 experiment picker 행(training_experiment_id 가 제출 입력 키다).
// 생성과 노트 수정도 같은 shape 를 돌려준다.
export type ExperimentOptionView = {
  training_experiment_id: string;
  name: string;
  description: string | null;
};

export type ResourceDefaultsView = {
  nodes: number;
  gpus_per_node: number;
};

// submit 초기 옵션 묶음. unavailable[] 은 partial fan-out 에서 실패한 섹션 이름이다(graceful).
export type SubmitOptionsView = {
  branches: BranchOptionView[];
  trainer_images: TrainerImageOptionView[];
  eval_scenarios: ScenarioOptionView[];
  workspaces: WorkspaceOptionView[];
  resource_defaults: ResourceDefaultsView;
  unavailable: string[];
};

export type BranchConfigEntryView = {
  path: string;
  size: number;
};

export type BranchConfigsView = {
  ref: string;
  sha: string;
  configs: BranchConfigEntryView[];
};

export type BranchConfigView = {
  ref: string;
  sha: string;
  path: string;
  size: number;
  body: string;
};

export async function getTrainingSubmitOptions(): Promise<SubmitOptionsView> {
  const response = await apiClient.get<SubmitOptionsView>(apiRoutes.training.submitOptions);
  return response.data;
}

// branch 를 고르면 그 ref 의 config 목록을 받는다. repo 를 생략하면 BE 기본 repo 를 쓴다.
export async function listTrainingBranchConfigs(
  ref: string,
  repo?: string,
): Promise<BranchConfigsView> {
  const params: Record<string, string> = { ref };
  if (repo) {
    params.repo = repo;
  }
  const response = await apiClient.get<BranchConfigsView>(apiRoutes.training.branchConfigs, {
    params,
  });
  return response.data;
}

// config 를 고르면 본문(inline YAML seed)을 받는다. 큰 파일이라 호출부가 timeout 을 넉넉히 둔다.
export async function getTrainingBranchConfig(
  ref: string,
  name: string,
  repo?: string,
): Promise<BranchConfigView> {
  const params: Record<string, string> = { ref, name };
  if (repo) {
    params.repo = repo;
  }
  const response = await apiClient.get<BranchConfigView>(apiRoutes.training.branchConfig, {
    params,
  });
  return response.data;
}

// branch-commits 목록의 한 항목. 위저드 Commit 드롭다운의 한 줄이다. `sha` 는 제출할 때
// `learning_core.ref` 로 보내는 값(40-hex)이다. `short_sha` 는 표시용이다. `committed_at` 은
// committer date 다. 표시할 때는 다른 시각처럼 `parseUtcMs` 로 변환한다.
export type BranchCommitView = {
  sha: string;
  short_sha: string;
  subject: string;
  author: string;
  committed_at: string;
};

// ref 에서 도달할 수 있는 최근 커밋. `sha` 는 해석된 head 이고 목록의 첫 항목이다.
// `since` 를 보낸 조회만 `since`·`since_reached` 를 돌려준다. 이때 목록은 head 에서 그 커밋까지
// (포함)다. `since_reached=false` 는 그 커밋이 `limit` 범위 밖이라 최신 `limit` 개만 왔다는
// 뜻이다. 두 키가 없으면 구간 조회가 아니라는 뜻이다. 별도 분기는 없다.
export type BranchCommitsView = {
  ref: string;
  sha: string;
  since?: string | null;
  since_reached?: boolean | null;
  commits: BranchCommitView[];
};

export type ListBranchCommitsOptions = {
  repo?: string;
  // BE 기본 50, 최대 200.
  limit?: number;
  // full SHA 만 받는다. 접두는 BE 가 422 로 거절한다.
  since?: string;
};

// branch(또는 SHA)를 고르면 그 ref 에서 도달하는 최근 커밋을 받는다. 생략한 옵션은 params 에
// 넣지 않는다.
export async function listTrainingBranchCommits(
  ref: string,
  options: ListBranchCommitsOptions = {},
): Promise<BranchCommitsView> {
  const params: Record<string, string | number> = { ref };
  if (options.repo) {
    params.repo = options.repo;
  }
  if (options.limit !== undefined) {
    params.limit = options.limit;
  }
  if (options.since !== undefined) {
    params.since = options.since;
  }
  const response = await apiClient.get<BranchCommitsView>(apiRoutes.training.branchCommits, {
    params,
  });
  return response.data;
}

// ===========================================================================
// 세션 제출·상세·명령 (training session id)
// ===========================================================================

export type LearningCoreReq = {
  kind: "branch" | "wheel";
  ref: string;
  repo?: string | null;
};

// config 소스는 repo_config(레시피 config 이름) 또는 inline_yaml(직접 본문)이다. 서버에 기본값이
// 있어서 모든 항목이 선택이다. 불변식은 BE 가 422 로 강제한다(repo_config↔inline_yaml 배타).
export type ConfigReq = {
  source?: "repo_config" | "inline_yaml";
  name?: string;
  overrides?: JsonObject;
  inline_yaml?: string | null;
};

// 제출 자원 스펙. 스케줄링 노브 둘은 **읽기와 같은 모양의 1급 필드**다. 그래서 재제출은 읽은
// 값을 그대로 보낼 수 있다(`TrainingSessionRecipeResourcesView` 와 짝). 미지정은 키를 빼서
// 표현한다. 키가 없으면 "상류 클러스터 기본을 따른다" 는 뜻이고, 명시 값과 다른 상태다.
export type ResourcesReq = {
  nodes?: number;
  gpus_per_node?: number;
  // false(명시 거부)와 부재(클러스터 기본)는 다르다. `?? false` 같은 기본값으로 바꾸지 않는다.
  allow_spot?: boolean;
  // 노드 잔여 수명 하한(초). `0` 은 "하한 없음" 을 뜻하는 값이다. 부재와 다르다.
  min_remaining_lifetime_seconds?: number;
  // registry 가 해석하지 않고 그대로 전달하는 값. 위 명시 필드를 여기에도 넣으면 BE 가 출처가
  // 둘이라며 거부한다(`ResourcesReq._no_explicit_in_extra`). 그래서 노브가 소유하는 키는 목록에서
  // 거른 뒤 보낸다.
  extra?: JsonObject;
};

// 학습 입력 공용 Dataset 선택 한 항목. 역할과 샘플링 비율을 담는다. training 은 `reference` 도
// 받지만, registry 는 학습에 실제로 쓰이는 두 역할만 허용한다.
export type DatasetSelectionReq = {
  dataset_uuid: string;
  role: "train" | "val";
  // 멀티 train 데이터셋 샘플링 비율(0 초과). **미지정이면 키를 보내지 않는다.** 그러면 training 이
  // 균등(1.0)으로 정한다. train 이 하나뿐이면 비율은 의미가 없다.
  weight?: number;
  // 배경 분리에 쓸 어노테이션 세트. 세트는 `AnnotationSet.registry_dataset_uuid` 로 특정
  // Dataset 에 묶인다. 그래서 세션 레벨이 아니라 **입력별 축**이다. 지정하면 BE 가 학습 앞에
  // mask 생성 preprocess step 을 추가한다. weight 와 같이 **미지정이면 키를 보내지 않는다**
  // (BE 스키마가 extra=forbid).
  //
  // 게이트는 데이터셋 등록보다 **먼저** 실행된다. 거절은 세 경우다:
  //   - 404 `training.annotation_set_not_found`(부재·삭제·타 Dataset 소속)
  //   - 409 `training.annotation_set_not_committed`(draft)
  //   - 409 `training.annotation_set_not_materialized`(렌더 미완)
  annotation_set_uuid?: string;
};

// 세션 표시 이름 상한. 값이 training `Session.display_name` 컬럼(varchar 120)에 그대로 저장된다.
// 그래서 그 폭이 실제 상한이다. 입력에서 막지 않으면 확정 단계에서 상류가 422 로 실패한다.
export const SESSION_DISPLAY_NAME_MAX_LENGTH = 120;

// Finetune 시작점. 참조는 카탈로그 policy 이거나 소스 세션이다. BE 는 checkpoint_uri 직접
// 지정과 session kind 를 의도적으로 허용하지 않는다(가시성 판정 불가 / training 미구현).
//
// `finetune_from_checkpoint` 도 URI 가 아니라 세션 id 를 받는다. BE 가 스코프 게이트를 통과한
// 뒤 상류에서 최신 raw 체크포인트 URI 를 해석한다. URI 는 브라우저로 오지 않는다.
export type TrainingInitReq =
  | {
      strategy: "finetune_from_policy";
      policy_id: string;
    }
  | {
      strategy: "finetune_from_checkpoint";
      source_session_id: string;
    };

export type IsaacEvaluationScenarioReq = {
  scenario_id: string;
  name: string;
  seed?: number | null;
};

export type IsaacEvaluationReq = {
  scenarios: IsaacEvaluationScenarioReq[];
};

// Config 전문(inline_yaml 본문)은 오지 않는다. 전문은 mlflow_run_id 아티팩트 조회로만 받는다.
type TrainingSessionRecipeConfigView = {
  source?: ConfigReq["source"] | null;
  name?: string | null;
  // 부재/null 은 "override 기록 없음"이고 `{}` 는 "명시적 0건"이다. 재제출 프리필이 두 상태를
  // 같게 취급하면 원본에 없던 팀 관례 시드가 다시 들어간다. `?? {}` 로 바꾸지 않는다.
  overrides?: JsonObject | null;
};

// 제출 스냅샷의 자원·스케줄링 노브. placement_profile 도 wire 에 오지만 표시를 1.1 로
// 미뤄서 이 타입에 넣지 않는다. 이 타입은 FE 가 쓰는 필드만 모델링한다(ZERG-249 08-12 코멘트).
type TrainingSessionRecipeResourcesView = {
  nodes?: number | null;
  gpus_per_node?: number | null;
  // 노드 잔여 수명 요구치(초). "이만큼 수명이 남은 노드에만 배치하라" 는 뜻이다. 관측값인
  // TrainingSessionDetailView.node_lifetime 과 이름은 비슷하지만 다른 값이다.
  min_remaining_lifetime_seconds?: number | null;
  // false(명시 거부)와 null(미지정)이 다른 3-state 다. `?? true` 같은 기본값을 넣지 않는다.
  allow_spot?: boolean | null;
};

// 제출 스냅샷의 learning-core. `ref` 는 사용자가 제출한 값(브랜치 이름 또는 고정한 커밋)
// 그대로다. `sha` 는 빌더가 그 ref 를 해석한 커밋이다. 요청 타입(`LearningCoreReq`)과 나눈
// 이유는 `sha` 가 응답에만 있는 축이기 때문이다. 제출 payload 에는 없다. 빌더가 아직 해석하지
// 않은 세션과 구서버 응답에는 `sha` 가 없다. 그때는 ref 만 표시한다.
export type SessionRecipeLearningCoreView = {
  kind: "branch" | "wheel";
  ref: string;
  repo?: string | null;
  sha?: string | null;
};

// 세션 제출 당시 레시피의 읽기 전용 스냅샷. detail 응답은 recipe를 생략하거나 null로
// 반환할 수 있다. 그래서 선택 필드로 읽는다. `learning_core`와 `resources`도 생략되거나
// null일 수 있다.
export type TrainingSessionRecipeView = {
  learning_core?: SessionRecipeLearningCoreView | null;
  config: TrainingSessionRecipeConfigView;
  resources?: TrainingSessionRecipeResourcesView | null;
};

// 제출 스냅샷의 config 출처를 반환한다. 모르면 null 이다. `source` 판정은 이 함수만 소유한다.
//
// **이름으로 출처를 추론하지 않는다.** inline_yaml 제출도 `name` 을 함께 보낸다(`buildConfigReq`).
// 그래서 "이름이 있으니 repo_config" 는 성립하지 않는다. 그렇게 판정하면 직접 편집해 제출한
// 세션이 repo 출처로 표기된다. 부재는 정보가 아니므로 null 로 둔다. 표시·판정하는 쪽이
// "모른다" 를 자기 문구로 표시한다.
export function recipeConfigSource(
  config: TrainingSessionRecipeView["config"] | null | undefined,
): ConfigReq["source"] | null {
  return config?.source ?? null;
}

// 제출 스냅샷의 스케줄링 노브. 미지정은 null 이다. 두 축의 3-상태 판정은 이 함수만 소유한다.
export type RecipeSchedulingKnobs = {
  // 부재/null 은 "클러스터 기본을 따름"이고 `false` 는 명시 거부다. 상류 기본은 허용이다.
  // 그래서 부재를 `false` 로 바꾸면 재제출이 경고 없이 스팟을 끈다. 값을 만들지 않고 null 로 둔다.
  allowSpot: boolean | null;
  // 부재는 "하한 없음" 이 아니다. 값이 없으면 상류가 4시간을 넣는다. `0` 만 하한 없음이다.
  // 그래서 두 상태를 같게 취급하면 안 된다.
  minLifetimeSeconds: number | null;
};

// 부재와 명시 값을 구분하는 코드를 한 곳에 둔다. 표시·프리필이 각자 `?? ` 를 쓰면, 한쪽이
// 부재를 기본값으로 바꿔도 다른 쪽과 달라진 것을 알 수 없다.
export function recipeSchedulingKnobs(
  recipe: TrainingSessionRecipeView | null | undefined,
): RecipeSchedulingKnobs {
  const resources = recipe?.resources;
  return {
    allowSpot: resources?.allow_spot ?? null,
    minLifetimeSeconds: resources?.min_remaining_lifetime_seconds ?? null,
  };
}

// 세션 제출 요청. 학습 입력은 공용 Dataset 1개 이상이다(개수 상한 없음). 서버가 각 primary
// anchor prefix 를 training-infra 에 지연 등록(reg-ds-{uuid})한다. 그래서 byte 복사·빌드 단계가
// 없다. workspace/experiment 는 불투명 training id다. workspace는 컨트롤러가 actor 매핑에서
// 정한다. 사용자는 experiment만 고른다. Idempotency-Key 는 헤더로 따로 보낸다.
export type SubmitTrainingSessionReq = {
  // target_dataset_index 는 이 목록 순서로 센다. 같은 Dataset 을 비율만 바꿔 두 번 넣을 수 있다.
  // 그래서 항목은 uuid 로 유일하지 않다.
  datasets: DatasetSelectionReq[];
  // 학습 타깃. 샘플링은 데이터셋 크기를 무시하고 weight 비율만 따른다. 1 epoch 은 이 데이터셋을
  // 한 번 다 읽는 길이가 된다. 값은 `datasets` 배열의 0-based 위치다. train 이 2개 미만이면 BE 가
  // 422 를 준다.
  target_dataset_index?: number | null;
  learning_core: LearningCoreReq;
  config?: ConfigReq;
  resources?: ResourcesReq;
  // 값이 없으면 init_strategy 는 scratch 다.
  init?: TrainingInitReq | null;
  // 자동 재시도가 이어서 실행할지 정한다. attempt 2회차부터 직전 attempt 의 durable checkpoint
  // 에서 이어간다. finetune 제출(`init`)에는 효과가 없다. 상류가 fine-tune entry 로 실행해 resume
  // 경로를 쓰지 않는다. 경고나 에러 없이 무시된다. 그래서 폼이 finetune 이면 이 값을 false 로
  // 보낸다.
  resume_on_retry?: boolean;
  session_type?: "train_only";
  training_workspace_id: string;
  training_experiment_id: string;
  training_experiment_name?: string | null;
  display_name?: string | null;
  channel_selection?: ChannelSelection | null;
  isaac_evaluation?: IsaacEvaluationReq | null;
  // 이 실행의 종료 알림 정책. **키가 없으면 위임**이다. 그러면 상류 workspace 기본 설정을 따른다.
  // 값의 3-상태와 조립은 trainingNotification 경계가 소유한다.
  notifications?: NotificationOverride;
};

// 제출할 때 추가될 override 를 미리 보는 요청. 제출과 같은 재조립 함수를 실행한다(부작용 없음).
// target_dataset_index 는 받지 않는다. 추가되는 override 는 입력들의 교집합 채널로 정해지고,
// 타깃과 관계없다.
export type PreviewOverridesReq = {
  datasets: DatasetSelectionReq[];
  learning_core: LearningCoreReq;
  config?: ConfigReq;
  channel_selection?: ChannelSelection | null;
};

// 제출 입력 config 경고 검사 요청. submit 본문과 모양이 같지만 cross-field 하드 검증이 없다.
// 작성 중인 폼의 입력도 422 가 아니라 finding 으로 돌아온다. 그래서 폼이 미완성이어도 부를 수
// 있다. workspace·experiment·display_name·isaac 는 검사 축이 아니라 받지 않는다.
export type ValidateTrainingSessionReq = {
  datasets: DatasetSelectionReq[];
  target_dataset_index?: number | null;
  learning_core: LearningCoreReq;
  config?: ConfigReq;
  resources?: ResourcesReq;
  init?: TrainingInitReq | null;
  channel_selection?: ChannelSelection | null;
};

// 경고 한 건. 모두 경고라 제출을 막지 않는다. code 별 화면 문구는 앱이 소유한다
// (제출 위저드 trainingValidationMessage).
export type TrainingValidationFinding = {
  code: string;
  message: string;
  // 문제가 된 override/config 키. 키 축이 아닌 finding 은 null 이다.
  config_key: string | null;
};

// 경고 리포트. 항상 200 이다. 검사 실패는 HTTP 에러가 아니라 findings 로 온다.
export type TrainingValidationReport = {
  findings: TrainingValidationFinding[];
  // false 는 config YAML 을 받지 못해 config 의존 축(override 키·cross-key)을 생략한 부분
  // 결과다. "문제 없음" 이 아니다.
  config_available: boolean;
};

// 입력 하나의 팔 구성. 입력마다 다를 수 있어 값을 합치지 않는다. null 은 미판정이다.
// 미판정 값을 한 값으로 묶으면 "섞인 구성" 경고를 낼 수 없다. 그래서 판정된 값과 구분한다.
export type DatasetArmLayout = {
  dataset_uuid: string;
  arm_layout: ArmLayout | null;
};

// 미리보기 응답. overrides 는 "실제 제출 payload 에 들어갈" 최종 override 집합이다.
export type OverridePreview = {
  overrides: JsonObject;
  injected_topics: string[];
  // 교집합 채널 기준 제안. 모든 제출 입력에 같은 모양으로 있는 채널만 담는다.
  suggested_selection: Record<string, string[]>;
  warnings: string[];
  arm_layouts: DatasetArmLayout[];
};

// training 데이터셋의 registry 원본 참조. 공용 Dataset 상세로 링크한다. BE 가 training 식별자
// (등록명·등록 id)를 해석한다. 원본 행이 있을 때만 이 값을 준다.
export type RegistryDatasetRef = {
  uuid: string;
  display_name: string;
  // 대표 섬네일 presigned URL. signer 를 받은 응답(Policy 카탈로그)에서만 값이 있다.
  // 이미지가 없으면 null 이고, 이니셜 placeholder 로 degrade 한다(목록을 다시 조회하지 않는다).
  thumbnail_url: string | null;
  thumbnail_url_expires_at: string | null;
};

// 제출 입력 dataset 의 usage 재구성 스냅샷. registry_dataset_uuid 는 제출 저널 항목에만 있다.
// 저널이 없는 세션의 recipe 파생 항목은 null 이다. registry_ref 가 없으면 uuid 유무에 따라
// 삭제된 데이터셋 또는 training-infra 전용 표기로 표시한다.
//
// 목록 순서가 제출 순서다. 같은 데이터셋을 비율만 바꿔 두 번 넣을 수 있다. 그래서 항목은
// 이름으로 유일하지 않다.
export type SubmittedDatasetRef = {
  registry_dataset_uuid: string | null;
  // 상류 카탈로그 이름. training 에서 직접 만든 데이터셋의 실제 이름이다(`alba_v3`).
  // registry 내부 등록 이름이면 서버가 null 로 바꾼다(내부 이름공간).
  upstream_dataset_label: string | null;
  role: string;
  // null 은 미지정이다. training 이 균등 비율을 적용했다는 뜻이다.
  weight?: number | null;
  is_target?: boolean;
  // 배경 분리에 쓴 어노테이션 세트. 제출 저널이 있는 세션에만 값이 있다. training UI 에서 직접
  // 만든 세션의 recipe 파생 항목은 registry 세트를 쓸 수 없어 항상 null 이다.
  annotation_set_uuid?: string | null;
  registry_ref?: RegistryDatasetRef | null;
};

// 세션 입력 쪽 조회의 한 항목. 입력 참조에 제출 순서를 더한다. 같은 Dataset 을 두 번 넣을 수
// 있으므로 uuid 가 아니라 position 이 한 세션 안에서 항목의 키다.
export type TrainingSessionDatasetItem = SubmittedDatasetRef & {
  // 제출 배열에서의 순서(0부터).
  position: number;
};

export type TrainingSessionDatasetQuery = PageQuery & {
  // Dataset 표시 이름 부분일치. 삭제된 Dataset 은 표시 이름이 없어 검색어가 있으면 빠진다.
  search?: string;
};

// 제출/명령(cancel·retry) 응답 view. status 는 training raw 어휘다(null 가능).
// experiment_display_name·created_at 은 제출 저널 스냅샷이다. 그래서 training UI 에서 직접 만든
// 세션에서는 null 이다. datasets 는 기본적으로 저널 스냅샷이다. 저널이 없는 세션은 상세·명령
// 경로가 training recipe 의 train 입력으로 채운다.
export type TrainingSessionView = {
  training_session_id: string;
  status: string | null;
  display_name: string | null;
  experiment_display_name: string | null;
  workspace_id: string | null;
  detail_path: string;
  datasets: SubmittedDatasetRef[];
  created_at: string | null;
};

// 세션이 만든 체크포인트(상세에 포함). epoch 은 체크포인트 태그(`epoch_NNN`/`step_NNNNNNN`)다.
// 상류는 run 의 checkpoints/ 산출물과 카탈로그에 등록된 정책의 artifact 를 함께 나열한다.
// 그래서 artifact 가 태그 디렉터리 밖이면 epoch 이 null 로 온다. 다운로드는 태그로만 찾으므로
// 이때 download_path 도 null 이다. 그 항목은 artifact_uri 로 등록만 할 수 있다. artifact_uri 는
// 체크포인트 artifact 의 gs:// 위치다. 이 체크포인트를 정책 카탈로그에 등록
// (registerTrainingPolicy)할 때 멱등 키로 전달한다.
export type SessionCheckpointView = {
  epoch: string | null;
  size_bytes: number | null;
  source: string | null;
  download_path: string | null;
  artifact_uri: string | null;
};

// 다운로드 가능 판정. 태그가 있는 항목만 다운로드할 수 있다. FE 는 download_path 를 읽지 않고
// epoch 으로 signed URL 경로를 조립한다(getTrainingSessionPolicyDownload). 그래서 판정 키도 epoch
// 하나다(BE 는 둘을 함께 비운다).
export function isDownloadableCheckpoint(
  checkpoint: SessionCheckpointView,
): checkpoint is SessionCheckpointView & { epoch: string } {
  return checkpoint.epoch !== null;
}

// Raw 파일 선택은 태그 대신 run/path를 사용한다. 갱신 시각·태그가 없어도 파일은 표시한다.
export type RawCheckpointView = {
  run_id: string;
  artifact_path: string;
  filename: string;
  checkpoint_tag: string | null;
  size_bytes: number | null;
  updated_at: string | null;
};

export type RawCheckpointDownloadView = {
  download_url: string;
  filename: string;
  size_bytes: number | null;
  expires_at: string;
};

// 부속 파일은 학습 run 이 checkpoint 와 함께 남기는 파일이다. 예: `config/dataset_stats.pt`.
// 대상 경로는 서버가 정한다. 남기지 않는 학습이 있어서 빈 목록은 정상이다.
export type CompanionFileView = {
  run_id: string;
  artifact_path: string;
  filename: string;
  size_bytes: number | null;
  updated_at: string | null;
};

export type CompanionFileDownloadView = {
  download_url: string;
  filename: string;
  size_bytes: number | null;
  expires_at: string;
};

export type PolicyDownloadView = {
  epoch: string;
  download_url: string;
};

export type NodeLifetimeState = "measured" | "uncapped" | "unknown" | "unavailable";

// 세션이 지금 쓰는 노드가 언제 사라지는지 나타낸다(관측 live). ETA 경고 배너가 이 값으로
// 판정한다. remaining_seconds 는 state === "measured" 에서만 숫자다(BE validator 가 고정).
// uncapped(상한 없는 on-demand)와 unknown·unavailable(모름)은 반대 의미다. 숫자만 보고 falsy 로
// 판정하면 "수명 무제한" 으로 잘못 읽는다. 판정은 반드시 state 를 먼저 본다.
export type SessionNodeLifetimeView = {
  state: NodeLifetimeState;
  remaining_seconds: number | null;
};

// rich 상세. 첫 render 1회로 개요·지표요약·eval요약·체크포인트를 함께 받는다(항목당 재호출 없음).
export type TrainingSessionDetailView = TrainingSessionView & {
  // 목록과 같은 현재 단계 스냅샷. BE 가 제공하기 전에는 목록에서 전달받은 값을 표시한다.
  progress?: TrainingSessionProgress | null;
  started_at: string | null;
  finished_at: string | null;
  mlflow_run_url: string | null;
  artifact_uri: string | null;
  metrics_summary: JsonObject | null;
  eval_summary: JsonObject | null;
  policies: SessionCheckpointView[];
  // policy fan-out 이 성공했는가. BE 는 `GET /sessions/{id}` 와 `.../policies` 를 병렬로 불러
  // 상세를 만든다. BE 는 **부분 실패를 허용**한다. policy 쪽이 실패하면 `policies=[]` 와
  // `policies_available=false` 를 넣고 나머지는 채워서 준다. 그래서 false 일 때의 빈 배열은
  // "산출물이 없다" 가 아니라 "읽지 못했다" 는 뜻이다(다시 시도하면 나올 수 있다).
  policies_available: boolean;
  // 관측 노드 잔여 수명. BE가 항상 4-state 값으로 채운다. 숫자는 measured에서만 온다.
  node_lifetime: SessionNodeLifetimeView;
  // 제출 스냅샷. training 상세는 recipe를 생략하거나 null로 반환할 수 있다.
  recipe?: TrainingSessionRecipeView | null;
  // 디버그 식별자가 없으면 목록 행 정보를 사용한다.
  experiment_id?: string | null;
  // MLflow 숫자 experiment id. host 힌트(mlflow_run_url)·run id 와 함께 run 딥링크를 조립하는
  // 키다. run 생성 전과 구서버 응답에는 없다. 그때는 host 링크로 폴백한다.
  mlflow_experiment_id?: string | null;
  // Config 전문 아티팩트 조회와 MLflow run 표시에 쓰는 단일 키. run이 아직 없으면 null이다.
  mlflow_run_id?: string | null;
};

// 체크포인트 목록의 세 상태. `policies` + `policies_available` 판정은 이 함수만 소유한다.
//
// **`length === 0` 만으로 판정하면 안 된다.** 조회 실패(unavailable)와 실제 없음(empty)은
// 둘 다 빈 배열로 오지만 사용자에게는 반대 사실이다. 조회 실패는 다시 시도하면 된다. 실제
// 없음은 학습이 아직 산출물을 만들지 않았다는 뜻이다. 둘을 한 경우로 묶으면, 상류가 잠깐
// 실패한 완료 세션을 "산출물 없음" 으로 읽는다.
export type SessionCheckpointsState = "unavailable" | "empty" | "listed";

// **명시 `false` 만 실패로 읽는다.** 타입은 필수지만 zod 가 없어 런타임에서는 보장하지 못한다.
// 구서버 응답에는 이 키가 없다. 부재를 실패로 판정하면 정상 세션도 모두 오류로 보인다
// (node_lifetime 이 부재를 unknown 으로 처리하는 것과 같은 이유). 키가 없으면 목록으로 판정한다.
export function sessionCheckpointsState(
  detail: Pick<TrainingSessionDetailView, "policies" | "policies_available">,
): SessionCheckpointsState {
  if (detail.policies_available === false) {
    return "unavailable";
  }
  return detail.policies.length === 0 ? "empty" : "listed";
}

// 세션 목록 쿼리(내/팀 공용). 페이징과 상태 카테고리·experiment 필터를 담는다.
//
// workspace 축은 두지 않는다. Me 스코프는 서버가 actor 로부터 도출한다. Team 은 별도의
// member_uuid 로, 선택한 멤버가 선점한 workspace 의 합집합으로 좁힌다. 워크스페이스는 연구자
// 화면의 어휘가 아니다(rules §15).
//
// statusGroup 은 표시 카테고리 하나(대기/진행 중/…)다. registry 가 카테고리를 멤버 raw status 로
// fan-out 한다. 그래서 FE 는 카테고리만 보낸다(계약: docs/specs/training-session-status-group.md).
// BE 는 raw status 단건 필터(`?status=`)도 지원하지만, 화면에 그 축이 없어 FE 는 쓰지 않는다.
//
// excludeStatusGroup 은 반대로 그 카테고리만 뺀 나머지다. 둘은 같은 축이다. 서버가 둘을 함께
// 지정하면 422 로 거절한다. 그래서 호출부는 하나만 채운다.
//
// includeDeleted 는 상태와 독립인 축이라 위 필터들과 함께 쓸 수 있다. 삭제 예정 세션을 목록에
// **추가한다**(삭제 예정만 남기는 축은 상류에 없다).
export type TrainingSessionListQuery = PageQuery & {
  // 표시 카테고리들의 합집합(반복키). 서버가 카테고리를 멤버 raw status 로 fan-out 한다.
  statusGroups?: readonly string[];
  excludeStatusGroup?: string;
  experimentId?: string;
  includeDeleted?: boolean;
  // registry 가 아래 세 축을 상류에 그대로 전달한다. branch/hash 는 부분일치다. dataset 은 제출 입력
  // 데이터셋을 본다.
  branch?: string;
  hash?: string;
  dataset?: string;
  // 아래 두 축(search, 학습 시작 시각 범위)은 상류가 이미 지원하지만 **registry 가 아직 전달하지
  // 않는다**. 서버가 모르는 키라 경고 없이 무시한다. 그래서 목록이 좁혀지지 않는다. registry 가
  // 전달하면 코드 변경 없이 동작한다.
  // 이름·노트 평문 검색. 데이터셋 목록과 같은 키 이름을 쓴다.
  search?: string;
  // 학습 시작 시각 범위(ISO 8601 date, 양끝 포함). 목록 행이 표시하는 생성 시각과 다른 축이다.
  // 제출(생성) 뒤 자원을 기다렸다가 나중에 시작하는 세션이 있다.
  startedAfter?: string;
  startedBefore?: string;
};

// 배열 축은 반복키로 보낸다(`?status_group=a&status_group=b`). apiClient 가 indexes: null 로
// 직렬화한다. 그래서 배열을 그대로 넣으면 된다. 빈 값·빈 배열은 키를 보내지 않는다.
//
// 서버가 배열로 선언한 키만 배열로 보낼 수 있다. 스칼라로 선언된 키에 반복키를 보내면 422 가
// 아니다. 서버는 **마지막 값 하나만** 받는다. 그러면 경고 없이 틀린 목록이 온다.
type SessionListParamValue = string | number | boolean | readonly string[];

function toSessionListParams(
  query: TrainingSessionListQuery,
): Record<string, SessionListParamValue> {
  const params: Record<string, SessionListParamValue> = {};
  if (query.page !== undefined) {
    params.page = query.page;
  }
  if (query.size !== undefined) {
    params.size = query.size;
  }
  if (query.statusGroups && query.statusGroups.length > 0) {
    params.status_group = query.statusGroups;
  }
  if (query.excludeStatusGroup) {
    params.exclude_status_group = query.excludeStatusGroup;
  }
  if (query.experimentId) {
    params.experiment_id = query.experimentId;
  }
  // 끌 때는 보내지 않는다. 서버 기본값과 같아서 쿼리·캐시 키를 늘릴 필요가 없다.
  if (query.includeDeleted) {
    params.include_deleted = true;
  }
  if (query.branch) {
    params.branch = query.branch;
  }
  if (query.hash) {
    params.hash = query.hash;
  }
  if (query.dataset) {
    params.dataset = query.dataset;
  }
  if (query.search) {
    params.search = query.search;
  }
  if (query.startedAfter) {
    params.started_after = query.startedAfter;
  }
  if (query.startedBefore) {
    params.started_before = query.startedBefore;
  }
  return params;
}

// 내 세션 목록. 내가 선점한 workspace 들의 세션이다(rich 행, 서버 페이징).
export async function listMyTrainingSessions(
  query: TrainingSessionListQuery = {},
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<TrainingSessionListView>> {
  const response = await apiClient.get<PageResp<TrainingSessionListView>>(
    apiRoutes.training.meSessions,
    { params: toSessionListParams(query), signal: options.signal },
  );
  return response.data;
}

// 팀 세션 목록. 선택 그룹(group_uuid)의 선점 workspace 세션이다(팀원 것 포함, rich 행, 서버
// 페이징).
export async function listTeamTrainingSessions(
  groupUuid: string,
  query: TrainingSessionListQuery = {},
  memberUuid?: string,
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<TrainingSessionListView>> {
  const response = await apiClient.get<PageResp<TrainingSessionListView>>(
    apiRoutes.training.groupSessions,
    {
      params: {
        ...toSessionListParams(query),
        group_uuid: groupUuid,
        ...(memberUuid ? { member_uuid: memberUuid } : {}),
      },
      signal: options.signal,
    },
  );
  return response.data;
}

// 내 스코프의 활성 건수. 아직 끝나지 않은 학습 세션 수와 eval-run 수다.
// 활성 기준은 BE 가 정한다. 상태 어휘는 training-infra 가 소유한다. FE 가 기준을 가지면
// 어휘가 늘 때 경고 없이 값이 틀린다.
export type TrainingActiveCounts = {
  training: number;
  evaluation: number;
  // 스케줄이 발화해 아직 끝나지 않은 세션 수. 자격이 없으면 0 이다. 키가 없는 것이 아니다.
  scheduled_sessions: number;
};

export async function getMyTrainingActiveCounts(): Promise<TrainingActiveCounts> {
  const response = await apiClient.get<TrainingActiveCounts>(apiRoutes.training.meActiveCounts);
  return response.data;
}

// 데이터셋 하나가 쓰인 학습 수. **registry 를 거친 제출만 센다.** 상류에서 직접 만든 세션과
// 그 세션의 복제·재시도는 제출 저널에 없어서 세지 않는다. 한 번도 쓰이지 않은 데이터셋은 행이
// 오지 않는다(0 을 채워 보내지 않는다).
export type TrainingDatasetUsage = {
  dataset_uuid: string;
  session_count: number;
};

export async function listMyDatasetUsage(): Promise<TrainingDatasetUsage[]> {
  const response = await apiClient.get<TrainingDatasetUsage[]>(apiRoutes.training.meDatasetUsage);
  return response.data;
}

export async function listTeamDatasetUsage(
  groupUuid?: string,
  memberUuid?: string,
): Promise<TrainingDatasetUsage[]> {
  const response = await apiClient.get<TrainingDatasetUsage[]>(
    apiRoutes.training.groupDatasetUsage,
    {
      params: {
        ...(groupUuid ? { group_uuid: groupUuid } : {}),
        ...(memberUuid ? { member_uuid: memberUuid } : {}),
      },
    },
  );
  return response.data;
}

// 세션 제출. idempotencyKey 는 헤더로 보낸다(필수). 재시도할 때 같은 키를 다시 써서 중복 생성을
// 막는다.
export async function previewTrainingOverrides(
  payload: PreviewOverridesReq,
): Promise<OverridePreview> {
  const response = await apiClient.post<OverridePreview>(
    apiRoutes.training.sessionsPreviewOverrides,
    payload,
  );
  return response.data;
}

// 제출 입력 config 경고 검사(부작용 없음, 항상 200). submit 은 같은 finding 을 서버 로그에만
// 남기고 제출을 막지 않는다. preview 와 같이 role 인자를 두지 않는다. BE 는 member-write
// 게이트지만, 위저드 진입이 이미 member 로 제한돼 있다.
export async function validateTrainingSession(
  payload: ValidateTrainingSessionReq,
): Promise<TrainingValidationReport> {
  const response = await apiClient.post<TrainingValidationReport>(
    apiRoutes.training.sessionsValidate,
    payload,
  );
  return response.data;
}

export async function submitTrainingSession(
  payload: SubmitTrainingSessionReq,
  idempotencyKey: string,
): Promise<TrainingSessionView> {
  const response = await apiClient.post<TrainingSessionView>(apiRoutes.training.sessions, payload, {
    headers: { "Idempotency-Key": idempotencyKey },
  });
  return response.data;
}

// rich 상세. 키는 training session id 다. 가시성은 workspace 선점 스코프(본인+같은 팀)다.
// 스코프 밖이면 404 다.
export async function getTrainingSession(sessionId: string): Promise<TrainingSessionDetailView> {
  const response = await apiClient.get<TrainingSessionDetailView>(
    apiRoutes.training.session(sessionId),
  );
  return response.data;
}

// 세션의 입력 Dataset 한 쪽. 순서는 목록 행과 같은 대표 순서(학습 타깃 → train → 제출 순)라 첫
// 항목이 목록 칸의 대표다. 섬네일 서명 URL 은 받은 쪽의 항목에만 있다. 스코프 밖이면 404 다.
export async function listTrainingSessionDatasets(
  sessionId: string,
  query: TrainingSessionDatasetQuery = {},
): Promise<PageResp<TrainingSessionDatasetItem>> {
  const response = await apiClient.get<PageResp<TrainingSessionDatasetItem>>(
    apiRoutes.training.sessionDatasets(sessionId),
    {
      params: {
        ...(query.page === undefined ? {} : { page: query.page }),
        ...(query.size === undefined ? {} : { size: query.size }),
        ...(query.search ? { search: query.search } : {}),
      },
    },
  );
  return response.data;
}

export async function cancelTrainingSession(sessionId: string): Promise<TrainingSessionView> {
  const response = await apiClient.post<TrainingSessionView>(
    apiRoutes.training.sessionCancel(sessionId),
  );
  return response.data;
}

export async function retryTrainingSession(sessionId: string): Promise<TrainingSessionView> {
  const response = await apiClient.post<TrainingSessionView>(
    apiRoutes.training.sessionRetry(sessionId),
  );
  return response.data;
}

// 체크포인트 1회용 다운로드 URL. byte 는 signed URL 로 직접 받는다(registry 는 중계하지 않음).
export async function getTrainingSessionPolicyDownload(
  sessionId: string,
  epoch: string,
): Promise<PolicyDownloadView> {
  const response = await apiClient.get<PolicyDownloadView>(
    apiRoutes.training.sessionPolicyDownload(sessionId),
    { params: { epoch } },
  );
  return response.data;
}

export async function getTrainingSessionRawCheckpoints(
  sessionId: string,
  options: { signal?: AbortSignal } = {},
): Promise<RawCheckpointView[]> {
  const response = await apiClient.get<RawCheckpointView[]>(
    apiRoutes.training.sessionRawCheckpoints(sessionId),
    { signal: options.signal },
  );
  return response.data;
}

export async function getTrainingSessionRawCheckpointDownload(
  sessionId: string,
  file: RawCheckpointView,
): Promise<RawCheckpointDownloadView> {
  const response = await apiClient.get<RawCheckpointDownloadView>(
    apiRoutes.training.sessionRawCheckpointDownload(sessionId),
    { params: { run_id: file.run_id, artifact_path: file.artifact_path } },
  );
  return response.data;
}

export async function getTrainingSessionCompanionFiles(
  sessionId: string,
  options: { signal?: AbortSignal } = {},
): Promise<CompanionFileView[]> {
  const response = await apiClient.get<CompanionFileView[]>(
    apiRoutes.training.sessionCompanionFiles(sessionId),
    { signal: options.signal },
  );
  return response.data;
}

export async function getTrainingSessionCompanionFileDownload(
  sessionId: string,
  file: CompanionFileView,
): Promise<CompanionFileDownloadView> {
  const response = await apiClient.get<CompanionFileDownloadView>(
    apiRoutes.training.sessionCompanionFileDownload(sessionId),
    { params: { run_id: file.run_id, artifact_path: file.artifact_path } },
  );
  return response.data;
}

// ===========================================================================
// 세션 관리 (training session id)
// ===========================================================================

export type SessionRenameReq = {
  display_name: string | null;
};

export type SessionRenameView = {
  id: string;
  display_name: string | null;
  mlflow_runs_synced: number;
};

export type SessionCloneView = {
  id: string;
  name: string | null;
  display_name: string | null;
  status: string;
  session_type: string | null;
};

// 이어서 학습 응답. 부모를 이어받아 새로 만든 세션이다(같은 세션을 in-place 로 쓰는 retry 와
// 다르다). 상류는 `name` 과 `display_name` 에 같은 값을 넣는다. 부모가 지정한 체크포인트 URI 는
// MLflow 내부 배치라 BE 가 전달하지 않는다.
export type SessionResumeView = {
  id: string;
  name: string | null;
  display_name: string | null;
  status: string;
  session_type: string | null;
  parent_session_id: string | null;
};

// 재학습 프로브 응답. 체크포인트 URI 는 포함하지 않는다. 있는지 여부만 답한다.
export type SessionRetrainAvailabilityView = {
  available: boolean;
};

export type SessionAppendStepsReq = {
  // 최소 1개(빈 배열은 422).
  steps: JsonObject[];
};

export type SessionAppendStepsView = {
  session_id: string;
  added_step_ids: string[];
  step_orders: number[];
};

export type BulkSessionAction = "cancel" | "delete" | "restore" | "tag" | "retry";

export type BulkSessionsReq = {
  action: BulkSessionAction;
  // 1..100 개.
  session_ids: string[];
  params?: JsonObject;
};

export type SessionBulkResultView = {
  session_id: string;
  status: string;
  detail: string | null;
};

export type SessionBulkSummaryView = {
  ok: number;
  skipped: number;
  error: number;
};

export type SessionBulkView = {
  action: string;
  results: SessionBulkResultView[];
  summary: SessionBulkSummaryView;
};

// rename. 키는 training session_id 다(스코프 멤버십 게이트). 상세 GET 과 경로 템플릿이 같지만
// PATCH·sid 를 쓴다.
export async function renameTrainingSession(
  sessionId: string,
  payload: SessionRenameReq,
): Promise<SessionRenameView> {
  const response = await apiClient.patch<SessionRenameView>(
    apiRoutes.training.session(sessionId),
    payload,
  );
  return response.data;
}

// 체크포인트 이어서 학습. 끝난 세션(succeeded/failed/cancelled)을 이어받는 새 세션을 만든다.
// 부모 workspace 의 GPU 를 쓴다. 그래서 cancel/retry 와 같은 선점자/admin 게이트다.
//
// 거절은 세 가지다:
//   - 409: 부모가 비터미널 · train step 2개 이상 · MLflow run 없음
//   - 404: 에폭을 하나도 끝내지 못해 재개할 체크포인트가 없음. 스코프 밖 404 와 같은 코드다.
//   - 503: MLflow 를 쓸 수 없음
// 호출부가 경우별로 문구를 고른다.
export async function resumeTrainingSession(sessionId: string): Promise<SessionResumeView> {
  const response = await apiClient.post<SessionResumeView>(
    apiRoutes.training.sessionResume(sessionId),
  );
  return response.data;
}

// 복제. 재현 시맨틱이다. 소스 recipe 를 그대로 다시 제출해 새 lineage 로 만든다. 소스와의
// edge 만 없다. finetune 세션이면 같은 부모 체크포인트에서 weights 를 읽는다.
// config overrides 는 받지 않는다. BE 가 non-empty body 를 422 로 거부한다. 그래서 본문 없이
// 보낸다. 이름도 본문에 넣지 않는다. 구 상류에 이름을 보내면 422 다(배포 순서에 따라 다름).
// 그래서 resume 처럼 만든 뒤 rename 으로 이름을 정한다.
export async function cloneTrainingSession(sessionId: string): Promise<SessionCloneView> {
  const response = await apiClient.post<SessionCloneView>(
    apiRoutes.training.sessionClone(sessionId),
  );
  return response.data;
}

// 재학습 가능 여부. 이 세션에 재개 가능한 raw 체크포인트가 있는지 확인한다. 제출 플로우 전용
// read 라 게이트가 member+ 다. 에폭을 하나도 끝내지 못한 세션이면 BE 가 상류 404 를 false 로
// 바꾼다. 상류 장애(502·503)는 바꾸지 않고 그대로 전달한다. 호출부가 이를 "확인 불가" 로 처리한다.
export async function getSessionRetrainAvailability(sessionId: string): Promise<boolean> {
  const response = await apiClient.get<SessionRetrainAvailabilityView>(
    apiRoutes.training.sessionRetrainAvailability(sessionId),
  );
  return response.data.available;
}

export async function appendTrainingSessionSteps(
  sessionId: string,
  payload: SessionAppendStepsReq,
): Promise<SessionAppendStepsView> {
  const response = await apiClient.post<SessionAppendStepsView>(
    apiRoutes.training.sessionSteps(sessionId),
    payload,
  );
  return response.data;
}

export async function bulkTrainingSessions(payload: BulkSessionsReq): Promise<SessionBulkView> {
  const response = await apiClient.post<SessionBulkView>(apiRoutes.training.sessionsBulk, payload);
  return response.data;
}

// 삭제. 세션을 목록에서 숨긴다. 상류 보존 기간이 지나면 산출물도 지워진다.
// 진행 중인 세션이면 상류가 취소도 함께 한다(cancel 캐스케이드). 단건도 이 경로를 쓴다.
// 상류가 bulk 만 제공해서 registry 도 단건 라우트를 두지 않는다.
//
// 게이트: 상류·registry 는 스코프 안 member+ 면 허용한다(cancel/retry 와 달리 선점자 게이트가
// 없다). 그러나 남의 학습을 취소하는 효과가 있다. 그래서 화면이 선점자/admin 으로 제한한다.
export async function deleteTrainingSessions(
  sessionIds: readonly string[],
): Promise<SessionBulkView> {
  return bulkTrainingSessions({ action: "delete", session_ids: [...sessionIds] });
}

// 복원. 삭제 예정 태그를 제거해 세션을 목록으로 되돌린다. 이미 삭제 예정이 아닌 세션은 상류가
// skipped 로 답한다(에러가 아니다). 취소된 학습이 다시 실행되지는 않는다. 삭제 전 상태로만
// 돌아간다.
export async function restoreTrainingSessions(
  sessionIds: readonly string[],
): Promise<SessionBulkView> {
  return bulkTrainingSessions({ action: "restore", session_ids: [...sessionIds] });
}

// 학습 write(submit·export) 전체의 팀 미매핑 게이트(403 training.no_team). actor 가 어느 training
// group 에도 매핑되지 않았을 때 온다. UI 는 "학습 팀 매핑 필요" 안내를 표시한다.
export function isNoTrainingTeamError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training.no_team";
}

// 본인이 선점하지 않은 workspace 를 조작할 때의 403(training.workspace_not_owned). 두 경우다:
// - submit·experiment 생성: submit-options 의 workspaces 가 이미 선점 기준으로 좁혀져 있다.
//   그래서 정상 경로에서는 나오지 않는다.
// - 그 workspace 의 GPU 를 쓰는 세션 write(cancel/retry 단건·bulk, clone·resume·steps):
//   팀 스코프라 남이 선점한 workspace 의 세션도 보인다. 그래서 화면을 연 뒤 선점이 바뀌면 정상
//   경로에서도 나온다. rename 은 GPU 를 쓰지 않아 스코프 게이트만 적용된다.
export function isWorkspaceNotOwnedError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training.workspace_not_owned";
}

// finetune 제출의 시작점 policy 가시성 게이트(404 training.policy_not_found). 부재·source 없음·
// 스코프 밖이 모두 같은 코드다(존재 leak 방지). 그래서 화면도 "권한 없음" 문구로 따로 구분하면
// 안 된다.
export function isTrainingPolicyNotFoundError(error: unknown): boolean {
  return isHttpClientError(error) && error.code === "training.policy_not_found";
}

// 제출에서 고른 어노테이션 세트의 게이트. 세 경우를 한 판별로 묶는다. 사용자가 할 일은 모두
// "세트를 다시 고른다" 로 같다. 어느 경우인지는 사유 문구가 아니라 카드의 후보 목록이 보여 준다.
// 404 부재·삭제·타 데이터셋 소속 / 409 draft / 409 렌더 미완.
export function isTrainingAnnotationSetUnusableError(error: unknown): boolean {
  return (
    isHttpClientError(error) &&
    (error.code === "training.annotation_set_not_found" ||
      error.code === "training.annotation_set_not_committed" ||
      error.code === "training.annotation_set_not_materialized")
  );
}
